import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {downloadDocument, fetchDocument} from './DocumentApi'

type PreviewState = {url: string; type: string; fileName: string} | null

/** Tarayıcının kendisi gösterebildiği türler; Word dosyaları yalnızca indirilebilir */
const isPreviewable = (type: string) => type === 'application/pdf' || type.startsWith('image/')

type Props = {
  documentId?: string | null
  /** İndirilen dosya adı bulunamazsa kullanılır */
  fileName: string
  size?: 'sm' | 'md'
}

/** CV Görüntüle + CV İndir butonları ve önizleme penceresi */
export const CvActions: FC<Props> = ({documentId, fileName, size = 'sm'}) => {
  const [loading, setLoading] = useState<'view' | 'download' | null>(null)
  const [preview, setPreview] = useState<PreviewState>(null)

  // Önizleme kapanınca bellekteki dosya bırakılır
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url)
    }
  }, [preview])

  if (!documentId) return null

  const handleView = async () => {
    setLoading('view')
    try {
      const {blob, fileName: name} = await fetchDocument(documentId, fileName)
      setPreview({url: URL.createObjectURL(blob), type: blob.type, fileName: name})
    } catch (error) {
      showErrorToast('CV açılamadı')
    } finally {
      setLoading(null)
    }
  }

  const handleDownload = async () => {
    setLoading('download')
    try {
      await downloadDocument(documentId, fileName)
    } catch (error) {
      showErrorToast('CV indirilemedi')
    } finally {
      setLoading(null)
    }
  }

  const buttonSize = size === 'sm' ? 'btn-sm' : ''

  return (
    <>
      <div className='d-flex gap-2'>
        <button type='button' className={`btn ${buttonSize} btn-light-info`} disabled={!!loading} onClick={handleView}>
          {loading === 'view' ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-eye'></i>}
          CV Görüntüle
        </button>
        <button
          type='button'
          className={`btn ${buttonSize} btn-light-primary`}
          disabled={!!loading}
          onClick={handleDownload}
        >
          {loading === 'download' ? (
            <span className='spinner-border spinner-border-sm' />
          ) : (
            <i className='bi bi-download'></i>
          )}
          CV İndir
        </button>
      </div>

      <Modal show={!!preview} onHide={() => setPreview(null)} size='xl' dialogClassName='modal-dialog-centered'>
        <div className='modal-header'>
          <h5 className='modal-title text-truncate'>{preview?.fileName}</h5>
          <div className='d-flex align-items-center gap-2'>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={handleDownload}>
              <i className='bi bi-download'></i>
              İndir
            </button>
            <button type='button' className='btn-close' onClick={() => setPreview(null)} />
          </div>
        </div>
        <div className='modal-body p-0 bg-light' style={{height: '78vh'}}>
          {preview && preview.type === 'application/pdf' && (
            <iframe title={preview.fileName} src={preview.url} className='w-100 h-100 border-0' />
          )}
          {preview && preview.type.startsWith('image/') && (
            <div className='w-100 h-100 d-flex align-items-center justify-content-center overflow-auto p-4'>
              <img src={preview.url} alt={preview.fileName} style={{maxWidth: '100%', maxHeight: '100%'}} />
            </div>
          )}
          {preview && !isPreviewable(preview.type) && (
            <div className='w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center p-10'>
              <i className='bi bi-file-earmark-word fs-3x text-primary mb-4'></i>
              <div className='fs-5 fw-semibold mb-2'>Bu dosya tarayıcıda görüntülenemiyor</div>
              <div className='text-muted mb-6'>Word (DOC/DOCX) dosyalarını indirip açabilirsiniz.</div>
              <button type='button' className='btn btn-primary' onClick={handleDownload}>
                <i className='bi bi-download'></i>
                İndir
              </button>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}
